import { useLayoutEffect, useState } from "react";

import {
  V2Button,
  V2CircularProgress,
  ArrowsInIcon,
  QuestionIcon,
  cn,
} from "@stacklet/ui";
import { useLocation, useSearchParams } from "react-router";

import { useHelpDrawer } from "./HelpDrawerContext";

interface Props {
  className?: string;
}

export function HelpDrawer({ className }: Props) {
  const { content, isLoading } = useHelpDrawer();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const helpParam = searchParams.get("help");
  const [isFooterVisible, setIsFooterVisible] = useState(false);

  const isHelpOpen = helpParam === "true";

  // Dynamic size when footer is visible or not
  // Re-run when location changes to handle cases where footer appears/disappears on route changes (api routes)
  useLayoutEffect(() => {
    const footer = document.querySelector("footer");
    if (!footer) {
      // Footer doesn't exist, state is already false by default
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsFooterVisible(entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      },
    );

    observer.observe(footer);

    return () => {
      observer.disconnect();
    };
  }, [location.pathname]);

  function handleCloseHelp() {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("help");
    setSearchParams(newParams);
  }

  return (
    <div
      aria-label="Help Drawer"
      className={cn(
        "fixed top-20 right-0 w-[20rem] z-50 pr-lg",
        isFooterVisible ? "bottom-28" : "bottom-3",
        "transform transition-all duration-300 ease-in-out will-change-transform",
        isHelpOpen ? "translate-x-0" : "translate-x-full pointer-events-none",
        className,
      )}
    >
      <div
        className={cn(
          "h-full",
          "bg-background-primary border border-overlay-medium shadow-sm rounded-sm",
          "flex flex-col",
        )}
      >
        <div className="flex flex-shrink-0 items-center justify-between border-b border-outline-decorative px-3 py-4">
          <div className="flex items-center gap-x-2 text-text-primary">
            <QuestionIcon className="h-5 w-5" />

            <div className="text-[1.75rem] font-bold">Help</div>
          </div>
          <V2Button
            aria-label="close-help-panel"
            onPress={handleCloseHelp}
            variant="plain"
          >
            <ArrowsInIcon />
          </V2Button>
        </div>
        <div className="flex flex-1 flex-col overflow-auto p-lg">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <V2CircularProgress
                aria-label="Loading help content"
                size="medium"
              />
            </div>
          ) : (
            content
          )}
        </div>
      </div>
    </div>
  );
}
